import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  Code,
  Copy,
  Database,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  FlaskConical,
  Headset,
  Key,
  Mail,
  Monitor,
  Star,
  Target,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../../app-routes';
import { PageHeader } from '../../components/common/PageHeader';
import { Card } from '../../components/ui/card';
import {
  partnerApi,
  type AcquireMetricsData,
  type EngageMetricsData,
  type MeasureMetricsData,
  type Partner,
  type ReputationMetricsData,
} from '../../lib/api';
import { fetchPartnerVoiceCallAggregateSummary } from '../voice/voice-calls-api';

export const PartnerMetricsPage = () => {
  return <OverviewPage />;
};

// ─── Overview Page ──────────────────────────────────────────────────────────

const OverviewPage = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState<string | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);

  const { data: partner } = useQuery<Partner>({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const { data: engageMetrics, isLoading: engageLoading } = useQuery<EngageMetricsData>({
    queryKey: ['engage-metrics', 0],
    queryFn: () => partnerApi.getEngageMetrics(0),
    staleTime: 5 * 60 * 1000,
  });

  const { data: acquireMetrics, isLoading: acquireLoading } = useQuery<AcquireMetricsData>({
    queryKey: ['acquire-metrics', 0],
    queryFn: () => partnerApi.getAcquireMetrics(0),
    staleTime: 5 * 60 * 1000,
  });

  const { data: measureMetrics, isLoading: measureLoading } = useQuery<MeasureMetricsData>({
    queryKey: ['measure-metrics', 0],
    queryFn: () => partnerApi.getMeasureMetrics(0),
    staleTime: 5 * 60 * 1000,
  });

  const { data: reputationMetrics, isLoading: reputationLoading } = useQuery<ReputationMetricsData>({
    queryKey: ['reputation-metrics'],
    queryFn: () => partnerApi.getReputationMetrics(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: voiceSummary, isLoading: voiceLoading } = useQuery({
    queryKey: ['voiceCallAggregateSummary-overview'],
    queryFn: () => fetchPartnerVoiceCallAggregateSummary(),
    staleTime: 5 * 60 * 1000,
  });

  const voiceActiveTenants = voiceSummary?.activeTenantCount ?? 0;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  // ─── Product card definitions ───────────────────────────────────────────

  const engageHasData =
    (engageMetrics?.summary.totalSends ?? 0) > 0 || (engageMetrics?.summary.activeSmbs ?? 0) > 0;

  const acquireHasData =
    (acquireMetrics?.summary.activeSmbs ?? 0) > 0 || (acquireMetrics?.summary.totalSpend ?? 0) > 0;

  const measureHasData =
    (measureMetrics?.summary.activeSmbs ?? 0) > 0 || (measureMetrics?.summary.totalSpend ?? 0) > 0;

  const reputationHasData = (reputationMetrics?.tenantsWithReputationEnabled ?? 0) > 0;

  const products = [
    {
      name: 'Engage',
      icon: Mail,
      activeIconColor: 'text-violet-600',
      activeBgColor: 'bg-violet-50',
      gradient: 'from-violet-200 to-violet-500',
      description: 'Targeted email & SMS campaigns with pre-built automation templates',
      hasData: engageHasData,
      isLoading: engageLoading,
      heroMetric: engageMetrics
        ? {
            value: String(engageMetrics.summary.activeSmbs),
            label: 'Active tenants',
          }
        : null,
      path: `${appRoutes.ENGAGE_SETUP}/usage`,
    },
    {
      name: 'Acquire',
      icon: Target,
      activeIconColor: 'text-blue-600',
      activeBgColor: 'bg-blue-50',
      gradient: 'from-blue-200 to-blue-500',
      description: 'AI-powered ad campaign creation and management for Google and Meta',
      hasData: acquireHasData,
      isLoading: acquireLoading,
      heroMetric: acquireMetrics
        ? {
            value: String(acquireMetrics.summary.activeSmbs),
            label: 'Active tenants',
          }
        : null,
      path: '/dashboard/acquire',
    },
    {
      name: 'Measure',
      icon: BarChart3,
      activeIconColor: 'text-emerald-600',
      activeBgColor: 'bg-emerald-50',
      gradient: 'from-emerald-200 to-emerald-500',
      description: 'Ad reporting dashboards with attribution tracking across all channels',
      hasData: measureHasData,
      isLoading: measureLoading,
      heroMetric: measureMetrics
        ? {
            value: String(measureMetrics.summary.activeSmbs),
            label: 'Active tenants',
          }
        : null,
      path: '/dashboard/measure',
    },
    {
      name: 'Reputation',
      icon: Star,
      activeIconColor: 'text-amber-600',
      activeBgColor: 'bg-amber-50',
      gradient: 'from-amber-200 to-amber-500',
      description: 'Automated review requests and feedback linked to Google Business Profiles',
      hasData: reputationHasData,
      isLoading: reputationLoading,
      heroMetric: reputationMetrics
        ? {
            value: String(reputationMetrics.tenantsWithReputationEnabled),
            label: 'Active tenants',
          }
        : null,
      path: '/dashboard/reputation',
    },
    {
      name: 'Assist',
      icon: Headset,
      activeIconColor: 'text-violet-600',
      activeBgColor: 'bg-violet-50',
      gradient: 'from-violet-200 to-violet-500',
      description: 'AI voice agent that answers incoming calls using your website data',
      hasData: voiceActiveTenants > 0,
      isLoading: voiceLoading,
      heroMetric:
        voiceActiveTenants > 0
          ? {
              value: String(voiceActiveTenants),
              label: 'Active tenants',
            }
          : null,
      path: appRoutes.VOICE,
    },
  ];

  // ─── Explore links ──────────────────────────────────────────────────────

  const exploreLinks = [
    {
      title: 'Documentation',
      description: 'Learn how to integrate and use EmbedReach features',
      icon: <BookOpen className="h-5 w-5" />,
      href: 'https://docs.embedreach.com',
    },
    {
      title: 'API Reference',
      description: 'Explore the full API reference and endpoints',
      icon: <FileText className="h-5 w-5" />,
      href: 'https://docs.embedreach.com/api-reference/introduction',
    },
    {
      title: 'Integration Guides',
      description: 'Step-by-step guides for common integrations',
      icon: <BookOpen className="h-5 w-5" />,
      href: 'https://docs.embedreach.com/samples/jwt-tokens',
    },
    {
      title: 'Testing Sandbox',
      description: 'Test your integration in a safe environment',
      icon: <FlaskConical className="h-5 w-5" />,
      href: 'https://docs.embedreach.com/embeddable-ui/iframe/sandbox',
    },
  ];

  return (
    <div className="space-y-10">
      <PageHeader title="Overview" description="Get started with integrating Reach into your platform" />

      {/* ── Product Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {products.map(product => {
          const isActive = product.hasData && product.heroMetric;
          const IconComponent = product.icon;

          return isActive ? (
            <div
              key={product.name}
              className={`rounded-xl bg-gradient-to-br ${product.gradient} p-[1.5px] cursor-pointer`}
              onClick={() => navigate(product.path)}>
              <Card className="p-6 flex flex-col h-full rounded-[calc(0.75rem-1.5px)] hover:shadow-md transition-shadow">
                <div
                  className={`h-9 w-9 ${product.activeBgColor} rounded-lg flex items-center justify-center mb-4`}>
                  <IconComponent className={`h-5 w-5 ${product.activeIconColor}`} />
                </div>

                <h3 className="text-sm font-semibold text-gray-900 mb-4">{product.name}</h3>

                <div className="flex-1">
                  <p className="text-3xl font-semibold text-gray-900 tracking-tight">
                    {product.heroMetric!.value}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{product.heroMetric!.label}</p>
                </div>
              </Card>
            </div>
          ) : (
            <Card
              key={product.name}
              className="p-6 flex flex-col hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(product.path)}>
              <div className="h-9 w-9 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <IconComponent className="h-5 w-5 text-gray-600" />
              </div>

              <h3 className="text-sm font-semibold text-gray-900 mb-4">{product.name}</h3>

              {product.isLoading ? (
                <div className="flex-1 flex items-start">
                  <div className="space-y-2">
                    <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  <p className="text-sm text-gray-500 flex-1">{product.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600">
                    Set up <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* ── Core Integration Points ─────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Core integration points</h2>
        <p className="text-sm text-gray-500 mb-4">
          Reach implementations have three core integration points, though not all products require all
          three.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 flex flex-col">
            <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <Monitor className="h-5 w-5 text-indigo-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Frontend UI Embedding</h3>
            <p className="text-xs text-gray-500 mb-2">Required for all products</p>
            <p className="text-sm text-gray-600 mb-4 flex-1">
              Implement JWT authentication and embed the Reach UI via iframe or React component.
            </p>
            <a
              href="https://docs.embedreach.com/onboarding/frontend"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
              Learn more <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Card>

          <Card className="p-6 flex flex-col">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Database className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Data Sync</h3>
            <p className="text-xs text-gray-500 mb-2">Required for all products</p>
            <p className="text-sm text-gray-600 mb-4 flex-1">
              Set up customer and transaction data syncing via APIs to power messaging and analytics.
            </p>
            <a
              href="https://docs.embedreach.com/onboarding/data-sync"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
              Learn more <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Card>

          <Card className="p-6 flex flex-col">
            <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <Code className="h-5 w-5 text-orange-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Attribution & Tracking</h3>
            <p className="text-xs text-gray-500 mb-2">Required for Measure, Acquire</p>
            <p className="text-sm text-gray-600 mb-4 flex-1">
              Install the lightweight JavaScript tracking snippet on your customers&apos; websites.
            </p>
            <a
              href="https://docs.embedreach.com/onboarding/tracking"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
              Learn more <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Card>
        </div>
      </div>

      {/* ── Getting Started ──────────────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Getting started</h2>
        <p className="text-sm text-gray-500 mb-4">
          Your credentials and environment details for development.
        </p>
        <Card className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Key className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Your environment variables</h3>
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm relative overflow-x-auto">
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    const envVars = `REACH_PARTNER_ID=${partner?.id || 'your-partner-id'}\nREACH_JWT_SECRET=${partner?.jwtSecret || 'your-jwt-secret'}`;
                    handleCopy(envVars, 'env');
                  }}
                  className="absolute top-3 right-3 p-1.5 rounded-md bg-gray-800 hover:bg-gray-700 transition-colors">
                  {copied === 'env' ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                <div className="space-y-1 pr-10">
                  <div className="text-gray-300">
                    <span className="text-gray-500">REACH_PARTNER_ID</span>
                    <span className="text-white">=</span>
                    <span className="text-green-400">{partner?.id || 'your-partner-id'}</span>
                  </div>
                  <div className="text-gray-300 flex items-center">
                    <span>
                      <span className="text-gray-500">REACH_JWT_SECRET</span>
                      <span className="text-white">=</span>
                      <span className="text-green-400">
                        {secretVisible
                          ? partner?.jwtSecret || 'your-jwt-secret'
                          : '••••••••••••••••••••••••••••••••'}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setSecretVisible(v => !v);
                      }}
                      className="ml-2 p-1 rounded hover:bg-gray-700 transition-colors flex-shrink-0"
                      title={secretVisible ? 'Hide secret' : 'Reveal secret'}>
                      {secretVisible ? (
                        <EyeOff className="h-3.5 w-3.5 text-gray-400" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/settings/api')}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
                  API configuration <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href="https://docs.embedreach.com/onboarding/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
                  Credentials guide <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Onboarding Phases ────────────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Onboarding guide</h2>
        <p className="text-sm text-gray-500 mb-4">
          Follow these phases to integrate Reach into your platform. For detailed technical
          specifications, refer to the{' '}
          <a
            href="https://docs.embedreach.com/api-reference/introduction"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline">
            API documentation
          </a>
          .
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              phase: '1-2',
              title: 'Credentials & Frontend',
              description: 'Get your credentials and embed the Reach UI into your platform',
              href: 'https://docs.embedreach.com/onboarding/credentials',
              icon: <Key className="h-5 w-5" />,
            },
            {
              phase: '3-4',
              title: 'Data Sync',
              description: 'Create schemas and sync customer and transaction data',
              href: 'https://docs.embedreach.com/onboarding/data-sync',
              icon: <Database className="h-5 w-5" />,
            },
            {
              phase: '5',
              title: 'Channel Setup',
              description: 'Configure email and SMS channels for Engage and Reputation',
              href: 'https://docs.embedreach.com/onboarding/channel-setup',
              icon: <Mail className="h-5 w-5" />,
            },
            {
              phase: '6',
              title: 'Automations',
              description: 'Set up automation templates and customer segments for Engage',
              href: 'https://docs.embedreach.com/onboarding/automations',
              icon: <FlaskConical className="h-5 w-5" />,
            },
            {
              phase: '7',
              title: 'Reputation',
              description: 'Enable internal feedback and Google review management',
              href: 'https://docs.embedreach.com/onboarding/reputation',
              icon: <Star className="h-5 w-5" />,
            },
            {
              phase: '8',
              title: 'Tracking',
              description: 'Install the attribution snippet for Acquire and Measure',
              href: 'https://docs.embedreach.com/onboarding/tracking',
              icon: <Code className="h-5 w-5" />,
            },
          ].map(phase => (
            <a
              key={phase.phase}
              href={phase.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group">
              <Card className="p-5 h-full hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 group-hover:text-blue-600 transition-colors mt-0.5">
                    {phase.icon}
                  </span>
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">Phase {phase.phase}</p>
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                      {phase.title}
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{phase.description}</p>
                  </div>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>

      {/* ── Resources ────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {exploreLinks.map(link => (
            <a
              key={link.title}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group">
              <Card className="p-5 h-full hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 group-hover:text-blue-600 transition-colors mt-0.5">
                    {link.icon}
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                      {link.title}
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{link.description}</p>
                  </div>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};
